import { cloneDeep } from "lodash-es";
// The same spacing constants the editor lays nodes out with, so a previewed flow and the saved
// flow are positioned identically rather than drifting if either gap is ever retuned.
import {
  ROUTE_VERTICAL_GAP,
  STRUCTURE_HORIZONTAL_GAP,
} from "@/flow/utils/incrementalAddShift";
// The editor's "Organize Steps" layout, shared rather than re-implemented, so the preview and the
// canvas arrange the same flow identically.
import { applyOrganizedLayout } from "@/flow/utils/organizedLayout";
// A saved flow is laid out the way the editor and the activity canvas lay one out: the backend's
// `ix`/`iy` are restored into step numbers, then recomputed as layout coordinates.
import { useAddPositionToStep } from "@/flow/application/useAddPositionToStep";
import { restoreStepNumbersFromIx } from "@/flow/utils/stepNumberUtils";
// The canvas reads loop membership out of uiData, so build it with the same helpers the editor
// uses rather than hand-writing the JSON.
import {
  createLoopEndUiData,
  createLoopStartUiData,
  createLoopStepUiData,
} from "@/flow/utils/uiDataUtils";
import {
  DEFAULT_LOOP_THREAD_COUNT,
  DEFAULT_LOOP_TYPE,
  normalizeLoopConfig,
} from "@/flow/utils/loopUtils";

const EPSILON = 0.001;

function formatStepName(step: any, displayNumber: number): string {
  if (typeof step?.name === "string" && step.name.trim()) {
    return step.name.trim();
  }

  if (typeof step?.action_id === "string" && step.action_id.trim()) {
    const parts = step.action_id.split("_");
    if (parts.length >= 2) {
      return `${parts[0].toUpperCase()} ${parts.slice(1).join("_")}`;
    }
    return step.action_id;
  }

  return `Step ${displayNumber}`;
}

/** A router step carries branches instead of an action. */
function isRouterStep(step: any): boolean {
  return Array.isArray(step?.routes) && step.routes.length > 0;
}

interface DraftContext {
  nextId: number;
}

function nextOrigId(ctx: DraftContext): string {
  ctx.nextId += 1;
  return `wizard-draft-step-${ctx.nextId}`;
}

function buildActionStep(
  step: any,
  origId: string,
  displayNumber: number,
  ix: number,
  x: number,
  y: number,
  iy = 0
): Record<string, any> {
  const connectorSpecId = step?.connector_id || "";

  return {
    id: origId,
    origId,
    uniqueId: origId,
    ix,
    iy,
    // Seed only. buildTopLevelStepList hands the finished list to applyOrganizedLayout, which
    // recomputes every position from ix/iy and loop nesting.
    position: { x, y },
    stepNumber:
      typeof step?.step_number === "number" && step.step_number > 0
        ? step.step_number
        : displayNumber,
    name: formatStepName(step, displayNumber),
    active: true,
    connectorSpecId,
    connection: {
      active: true,
      connector: { connectorSpecId },
    },
    parameters:
      step?.parameters && typeof step.parameters === "object"
        ? step.parameters
        : {},
    headers:
      step?.headers && typeof step.headers === "object" ? step.headers : {},
    input_mapping:
      step?.input_mapping && typeof step.input_mapping === "object"
        ? step.input_mapping
        : {},
    output_mapping:
      step?.output_mapping && typeof step.output_mapping === "object"
        ? step.output_mapping
        : {},
    // The canvas reads both under the names a saved step uses, so the delay badge and the filter
    // funnel light up on a previewed step exactly as they do on a persisted one. The wizard spec
    // names them `delay_ms` and `conditions`; the rename happens here rather than in the canvas.
    delay: typeof step?.delay_ms === "number" ? step.delay_ms : undefined,
    filter: Array.isArray(step?.conditions) ? step.conditions : [],
  };
}

/**
 * Builds one router preview node plus its branches.
 *
 * The canvas identifies a router by `type: "ROUTER"` and renders one labelled branch per entry in
 * `routes`, each branch owning its own `steps`. Without this the router falls through the action
 * path: it has no name and no connector, so it renders as a blank "Step N" and its branch steps
 * never appear at all.
 */
function buildRouterStep(
  step: any,
  ctx: DraftContext,
  origId: string,
  displayNumber: number,
  ix: number,
  x: number,
  y: number,
  iy = 0
): Record<string, any> {
  const routes = (step.routes as any[]).map(
    (route: any, routeIndex: number) => {
      const routeOrigId = nextOrigId(ctx);
      const branchY = y + (routeIndex + 1) * ROUTE_VERTICAL_GAP;

      return {
        id: routeOrigId,
        origId: routeOrigId,
        uniqueId: routeOrigId,
        parentId: origId,
        name:
          (typeof route?.name === "string" && route.name.trim()) ||
          `Condition ${routeIndex + 1}`,
        // The preview only needs the branch to look conditional; a route with no rows
        // would read as an unconditional branch. Both names are accepted for the same reason
        // summarizeStepFilter accepts both: the authored spec says `conditions` and the persisted
        // form says `filter`, and reading only one makes a conditional branch preview as
        // "otherwise".
        filter:
          [route?.filter, route?.conditions].find(
            (rows) => Array.isArray(rows) && rows.length
          ) ?? [],
        // Carried so the hover card can tell a fallback branch ("otherwise") apart from one
        // whose conditions simply failed to build — both arrive with an empty filter.
        default: Boolean(route?.default),
        ix: routeIndex,
        iy: routeIndex + 1,
        order: routeIndex,
        active: true,
        // One row down per nesting level, matching useAddPositionToStep's `iy = subPosition`.
        // Sibling branches deliberately share a row: applyOrganizedLayout is what fans them out,
        // by offsetting each branch below the previous one's deepest step.
        steps: buildStepList(
          Array.isArray(route?.steps) ? route.steps : [],
          ctx,
          x + STRUCTURE_HORIZONTAL_GAP,
          branchY,
          routeOrigId,
          iy + 1
        ),
      };
    }
  );

  return {
    id: origId,
    origId,
    uniqueId: origId,
    type: "ROUTER",
    ix,
    iy,
    position: { x, y },
    stepNumber:
      typeof step?.step_number === "number" && step.step_number > 0
        ? step.step_number
        : displayNumber,
    name: "router",
    active: true,
    routes,
  };
}

function buildStepList(
  specSteps: any[],
  ctx: DraftContext,
  baseX: number,
  baseY: number,
  parentId?: string,
  iy = 0
): Record<string, any>[] {
  return specSteps.map((step: any, index: number) => {
    const origId = nextOrigId(ctx);
    const x = baseX + index * STRUCTURE_HORIZONTAL_GAP + (index + 1) * EPSILON;
    const y = baseY + (index + 1) * EPSILON;
    const displayNumber = ctx.nextId;

    const built = isRouterStep(step)
      ? buildRouterStep(step, ctx, origId, displayNumber, index, x, y, iy)
      : buildActionStep(step, origId, displayNumber, index, x, y, iy);

    return cloneDeep(parentId ? { ...built, parentId } : built);
  });
}

/**
 * A flow-level loop block, spanning a run of top-level steps by their 1-based `step_number`.
 *
 * The wizard declares loops beside `steps` rather than nesting them, because the LOOP / LOOP_END
 * pair does not exist until the gateway expands the block.
 */
interface LoopBlock {
  loopId: string;
  firstStep: number;
  lastStep: number;
  sourcePath: string;
  parallelProcessing: boolean;
  threadCount: number;
}

/** The 1-based step numbers a loop block may address, in the order the steps appear. */
function topLevelStepNumbers(specSteps: any[]): Set<number> {
  return new Set(
    specSteps.map((step: any, index: number) =>
      typeof step?.step_number === "number" ? step.step_number : index + 1
    )
  );
}

function readLoopBlocks(flowSpec: any, specSteps: any[]): LoopBlock[] {
  const loops = Array.isArray(flowSpec?.loops) ? flowSpec.loops : [];
  const stepNumbers = topLevelStepNumbers(specSteps);
  const usedIds = new Set<string>();

  return loops
    .filter(
      (loop: any) =>
        typeof loop?.first_step === "number" &&
        typeof loop?.last_step === "number" &&
        loop.last_step >= loop.first_step &&
        // Both bounds must name a real step. A block that addresses a step number the flow does
        // not have can never be expanded: its LOOP would open and never close, marking every
        // later step as loop body, and its back edge would point at a node that was never built.
        // The gateway rejects such a block outright, so dropping it keeps the preview from
        // showing a loop that could not be saved.
        stepNumbers.has(loop.first_step) &&
        stepNumbers.has(loop.last_step)
    )
    .map((loop: any, index: number) => {
      // origIds are derived from the id, so a synthesized fallback must not land on an id another
      // block already claimed — that would give two LOOP nodes the same node id.
      const declared = String(loop?.loop_id || `loop_${index + 1}`);
      const loopId = usedIds.has(declared) ? `${declared}-${index}` : declared;
      usedIds.add(loopId);

      return {
        loopId,
        firstStep: loop.first_step,
        lastStep: loop.last_step,
        sourcePath:
          typeof loop?.source_path === "string" ? loop.source_path : "",
        parallelProcessing: Boolean(loop?.parallel_processing),
        threadCount:
          typeof loop?.thread_count === "number"
            ? loop.thread_count
            : DEFAULT_LOOP_THREAD_COUNT,
      };
    });
}

// Derived from loop_id rather than the running counter, so the LOOP_END and the body steps can
// name their LOOP before the walk reaches it.
const loopStartOrigId = (block: LoopBlock) =>
  `wizard-draft-loop-${block.loopId}`;
const loopEndOrigId = (block: LoopBlock) =>
  `wizard-draft-loop-end-${block.loopId}`;

type DraftSlot =
  | { kind: "step"; step: any }
  | { kind: "loopStart"; block: LoopBlock }
  | { kind: "loopEnd"; block: LoopBlock };

/**
 * Interleaves LOOP / LOOP_END markers into the top-level step order.
 *
 * Mirrors the gateway's expandWizardLoops: when several blocks open on the same step the widest
 * opens first, and when several close together the narrowest closes first, so an inner LOOP_END
 * always precedes its outer one.
 */
function expandLoopSlots(specSteps: any[], blocks: LoopBlock[]): DraftSlot[] {
  const width = (block: LoopBlock) => block.lastStep - block.firstStep;
  const slots: DraftSlot[] = [];

  specSteps.forEach((step: any, index: number) => {
    const stepNumber =
      typeof step?.step_number === "number" ? step.step_number : index + 1;

    blocks
      .filter((block) => block.firstStep === stepNumber)
      .sort((a, b) => width(b) - width(a) || a.loopId.localeCompare(b.loopId))
      .forEach((block) => slots.push({ kind: "loopStart", block }));

    slots.push({ kind: "step", step });

    blocks
      .filter((block) => block.lastStep === stepNumber)
      .sort((a, b) => width(a) - width(b) || a.loopId.localeCompare(b.loopId))
      .forEach((block) => slots.push({ kind: "loopEnd", block }));
  });

  return slots;
}

function buildLoopStartNode(
  block: LoopBlock,
  ix: number,
  x: number,
  y: number
): Record<string, any> {
  const origId = loopStartOrigId(block);

  return {
    id: origId,
    origId,
    uniqueId: origId,
    // The canvas renders the blue Loop node off this type, exactly as for a saved flow.
    type: "LOOP",
    name: "Loop",
    // The wizard only emits split loops; taking the constant keeps it from drifting from the editor.
    loopType: DEFAULT_LOOP_TYPE,
    // What the loop iterates over, in the wizard's authored form so the hover card can tag it.
    // A saved LOOP keeps the same value on `contentPath`, already lowered to the editor's encoding.
    sourcePath: block.sourcePath,
    // Through the same normaliser the editor persists with, so the preview cannot hold a config
    // shape the editor would never save — it is discriminated by loop type, and a FOR loop keeps
    // only `count` while a non-parallel type is reduced to the default.
    loopConfig: normalizeLoopConfig(
      {
        parallelProcessing: block.parallelProcessing,
        threadCount: block.threadCount,
      },
      DEFAULT_LOOP_TYPE
    ),
    ix,
    iy: 0,
    position: { x, y },
    active: true,
  };
}

function buildLoopEndNode(
  block: LoopBlock,
  ix: number,
  x: number,
  y: number
): Record<string, any> {
  const origId = loopEndOrigId(block);

  return {
    id: origId,
    origId,
    uniqueId: origId,
    type: "LOOP_END",
    name: "Loop End",
    ix,
    iy: 0,
    position: { x, y },
    active: true,
    uiData: createLoopEndUiData(loopStartOrigId(block)),
  };
}

/**
 * Builds the top-level chain with LOOP / LOOP_END nodes woven in.
 *
 * Branch steps use the plain {@link buildStepList} instead: a loop block addresses top-level
 * `step_number`s only, so a loop can never span steps inside a router branch.
 */
function buildTopLevelStepList(
  specSteps: any[],
  blocks: LoopBlock[],
  ctx: DraftContext,
  baseX: number,
  baseY: number
): Record<string, any>[] {
  const slots = expandLoopSlots(specSteps, blocks);
  const bodyOrigIdsByLoop = new Map<string, string[]>();
  const parentLoopByLoop = new Map<string, string>();
  // 1-based position of each LOOP node, which is the step number a `{{loop.item…}}` reference
  // resolves to once the gateway expands the block.
  const loopStepNumberByLoop = new Map<string, number>();
  blocks.forEach((block) => bodyOrigIdsByLoop.set(block.loopId, []));

  const openLoops: LoopBlock[] = [];
  const built: Record<string, any>[] = [];

  const recordBodyStep = (origId: string) => {
    const enclosing = openLoops[openLoops.length - 1];
    const body = enclosing && bodyOrigIdsByLoop.get(enclosing.loopId);
    if (body) {
      body.push(origId);
    }
    return enclosing;
  };

  slots.forEach((slot, index) => {
    const x = baseX + index * STRUCTURE_HORIZONTAL_GAP + (index + 1) * EPSILON;
    const y = baseY + (index + 1) * EPSILON;

    if (slot.kind === "loopStart") {
      // A nested LOOP is itself a body step of the block enclosing it.
      const enclosing = recordBodyStep(loopStartOrigId(slot.block));
      const loopNode = buildLoopStartNode(slot.block, index, x, y);
      if (enclosing) {
        parentLoopByLoop.set(slot.block.loopId, loopStartOrigId(enclosing));
        // applyOrganizedLayout reads this plain field (not uiData) to spot a loop nested inside
        // another one and drop it onto its own row; without it both loops share row 0 and their
        // back-edges are drawn on top of each other.
        loopNode.parentLoopOrigId = loopStartOrigId(enclosing);
      }
      loopStepNumberByLoop.set(slot.block.loopId, index + 1);
      built.push(loopNode);
      openLoops.push(slot.block);
      return;
    }

    if (slot.kind === "loopEnd") {
      // Close before recording: a LOOP_END belongs to its enclosing loops' bodies but not its own.
      const openIndex = openLoops.indexOf(slot.block);
      if (openIndex !== -1) {
        openLoops.splice(openIndex, 1);
      }
      recordBodyStep(loopEndOrigId(slot.block));
      built.push(buildLoopEndNode(slot.block, index, x, y));
      return;
    }

    const origId = nextOrigId(ctx);
    const displayNumber = ctx.nextId;
    const node = isRouterStep(slot.step)
      ? buildRouterStep(slot.step, ctx, origId, displayNumber, index, x, y)
      : buildActionStep(slot.step, origId, displayNumber, index, x, y);

    const enclosing = recordBodyStep(origId);
    if (enclosing) {
      node.uiData = createLoopStepUiData(loopStartOrigId(enclosing));
      // Body steps travel with their loop when applyOrganizedLayout shifts it down a row.
      node.parentLoopOrigId = loopStartOrigId(enclosing);
      // Lets the hover card render `{{loop.item.x}}` as the editor does — an output tag on the
      // enclosing LOOP's step number — instead of the raw marker.
      node.loopStepNumber = loopStepNumberByLoop.get(enclosing.loopId);
    }
    built.push(node);
  });

  // A LOOP lists its body in uiData, which is only known once the whole block has been walked.
  blocks.forEach((block) => {
    const node = built.find(
      (candidate) => candidate.origId === loopStartOrigId(block)
    );
    if (!node) {
      return;
    }
    node.uiData = createLoopStartUiData(
      loopEndOrigId(block),
      (bodyOrigIdsByLoop.get(block.loopId) || []).map((origId, stepIndex) => ({
        loopStepIndex: stepIndex,
        origId,
      })),
      parentLoopByLoop.get(block.loopId)
    );
  });

  // Final positions come from the editor's own "Organize Steps" engine, so a previewed flow is
  // laid out by the same rules (nested-loop rows, router branch offsets, column compaction) the
  // canvas applies when that button is pressed. It must run last: it reads the loop uiData built
  // just above to pair each LOOP with its LOOP_END.
  applyOrganizedLayout(built);

  return built.map((node) => cloneDeep(node));
}

/**
 * The loop's return arrow, mirroring the gateway's addWizardLoopBackEdgeFlowLines.
 *
 * Both ends use `__handle-top`, which is what the canvas's isLoopBackEdge check keys off, and
 * `animated` is what VueFlow's stylesheet turns into the dashes a hand-built loop draws.
 */
function draftLoopBackEdge(block: LoopBlock): Record<string, any> {
  const source = loopEndOrigId(block);
  const target = loopStartOrigId(block);

  return {
    id: `e${source}-${target}`,
    source,
    target,
    sourceHandle: `${source}__handle-top`,
    targetHandle: `${target}__handle-top`,
    type: "custom",
    data: "{}",
    animated: true,
  };
}

/**
 * One edge, with the same handles the gateway's generateFlowLine picks.
 *
 * A router is entered from the top and left from the bottom; every other step is entered from the
 * left and left from the right. Getting the target handle wrong would draw the edge into a router
 * differently here than on the saved flow, which is the one thing this preview exists to rule out.
 */
function draftFlowLine(
  source: string,
  target: string,
  fromRouter: boolean,
  toRouter = false
): Record<string, any> {
  return {
    id: `e${source}-${target}`,
    source,
    target,
    sourceHandle: `${source}__handle-${fromRouter ? "bottom" : "right"}`,
    targetHandle: `${target}__handle-${toRouter ? "top" : "left"}`,
    type: "custom",
    data: "{}",
    animated: fromRouter,
  };
}

/**
 * Builds the preview's edges, mirroring the backend's generateFlowLines.
 *
 * The canvas only derives edges itself when no flowLines are supplied, and that derivation does
 * not wire a router to its branches — so a previewed router would render with its branch steps
 * floating unconnected. Emitting the edges here keeps the preview identical to how the same flow
 * draws once saved.
 */
function buildDraftFlowLines(
  steps: Record<string, any>[]
): Record<string, any>[] {
  const lines: Record<string, any>[] = [];

  const walk = (list: Record<string, any>[]) => {
    list.forEach((step, index) => {
      if (Array.isArray(step.routes) && step.routes.length) {
        step.routes.forEach((route: any) => {
          const first = route.steps?.[0];
          if (first) {
            lines.push(draftFlowLine(step.origId, first.origId, true));
          }
          walk(route.steps || []);
        });
        return;
      }

      const next = list[index + 1];
      if (next) {
        lines.push(
          draftFlowLine(step.origId, next.origId, false, isRouterStep(next))
        );
      }
    });
  };

  walk(steps);
  return lines;
}

/**
 * Gives a saved flow the geometry it was stored without.
 *
 * A flow the wizard saved carries no layout: every step comes back with `iy: 0` and `position`
 * JSON `null` (measured on the stored demo flow 90db230b v2 — 18 rows, all of them). An
 * editor-saved flow of the same shape stores a real `{x, y}` per step and one `iy` per branch row,
 * which is why that one draws correctly and this one does not.
 *
 * The application closes the gap on READ, not on write, and it does it the same way in both places
 * that render a saved flow: `EditVueFlow.getSelectedFlow` and `ExecutionSummary.getFlowAndLogs`
 * both run the fetched steps through `restoreStepNumbersFromIx` (the backend's globally unique
 * `ix` is about to be overwritten, and the badge is read from `stepNumber`) and then
 * `useAddPositionToStep`, which rewrites `ix` as a layout column and `iy` as the branch row.
 *
 * That is the space `applyOrganizedLayout` consumes — `buildTopLevelStepList` builds the same two
 * fields for a new draft before calling it. Handing it the backend's numbers instead puts every
 * step on row 0 and spreads the columns by a globally unique `ix`, which is a single overlapping
 * line, not a layout.
 */
function positionSavedFlowSteps(flowSpec: any): Record<string, any>[] {
  const saved = cloneDeep(Array.isArray(flowSpec?.steps) ? flowSpec.steps : []);
  if (!saved.length) {
    return saved;
  }

  restoreStepNumbersFromIx(saved, {});
  const positioned = useAddPositionToStep(saved, cloneDeep(flowSpec.flowLines));
  applyOrganizedLayout(positioned);
  return positioned;
}

export function buildWizardDraftFlowSnapshot(
  flowSpec: any
): Record<string, any> {
  // An EDIT previews a flow that already EXISTS, and its topology lives in the flowLines the
  // backend persisted — not in the order of its steps. Deriving edges here cannot reproduce it:
  // buildDraftFlowLines treats a router as terminal at its level (true of an authored spec, not of
  // a saved flow, where a step can follow a router), and readLoopBlocks only accepts loop blocks
  // bounded by TOP-LEVEL step numbers, so a loop inside a router branch is never reconstructed.
  // Measured on flow 3170621e v4: 26 derived edges against the 41 the flow has, three `Loop End`
  // nodes with no incoming edge, and no loop nodes at all.
  //
  // So when the payload IS the saved flow — steps with their real origIds and the real flowLines,
  // marked by saved_flow_snapshot — hand it straight to the canvas, which is the same component
  // and the same data the editor itself renders. The derivation below stays for a NEW draft, where
  // there is no saved flow and no flowLines to read.
  if (flowSpec?.saved_flow_snapshot && Array.isArray(flowSpec?.flowLines)) {
    return {
      name:
        (typeof flowSpec?.flow?.name === "string" && flowSpec.flow.name) ||
        "Draft Flow",
      description:
        (typeof flowSpec?.flow?.description === "string" &&
          flowSpec.flow.description) ||
        "",
      active: true,
      latest: true,
      flowLines: cloneDeep(flowSpec.flowLines),
      steps: positionSavedFlowSteps(flowSpec),
    };
  }

  const steps = Array.isArray(flowSpec?.steps) ? flowSpec.steps : [];
  const loopBlocks = readLoopBlocks(flowSpec, steps);
  const ctx: DraftContext = { nextId: 0 };
  const draftSteps = buildTopLevelStepList(steps, loopBlocks, ctx, 0, 0.01);
  const flowLines = [
    ...buildDraftFlowLines(draftSteps),
    ...loopBlocks.map((block) => draftLoopBackEdge(block)),
  ];

  if (draftSteps.length) {
    flowLines.unshift({
      id: `e-start-${draftSteps[0].origId}`,
      source: "start",
      target: draftSteps[0].origId,
      type: "custom",
      data: "{}",
      animated: false,
    });
  }

  return {
    name:
      (typeof flowSpec?.flow?.name === "string" && flowSpec.flow.name) ||
      "Draft Flow",
    description:
      (typeof flowSpec?.flow?.description === "string" &&
        flowSpec.flow.description) ||
      "",
    active: true,
    latest: true,
    flowLines,
    steps: draftSteps,
  };
}

/** Total steps in a draft spec, counting the steps nested inside router branches. */
export function countWizardDraftSteps(flowSpec: any): number {
  const walk = (specSteps: any): number =>
    (Array.isArray(specSteps) ? specSteps : []).reduce(
      (total: number, step: any) =>
        total +
        1 +
        (Array.isArray(step?.routes)
          ? step.routes.reduce(
              (branchTotal: number, route: any) =>
                branchTotal + walk(route?.steps),
              0
            )
          : 0),
      0
    );

  return walk(flowSpec?.steps);
}
